import React from 'react';
import { useGate } from 'statsig-react';
import { gql } from '@soundxyz/gql-string';
import type { PostLinkSource } from '@soundxyz/utils/analytics';
import { type FragmentType, getFragment, ProfileCreatedPostFragmentDoc } from '@graphql/reactQuery';
import { FEATURE_GATES } from '@utils/constants';
import { PlaylistCreatedPost } from './PlaylistPost';
import { ReleaseCreatedPost } from './ReleasePost';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment ProfileCreatedPost on CreatedPost {
    id
    post {
      id
      __typename
      ... on ReleasePost {
        ...ListReleaseCreatedPost
      }
      ... on PlaylistPost {
        ...ListPlaylistCreatedPost
      }
    }
    ...UserInfoCreatedPost
  }
`);

export const ProfileCreatedPostActivity = ({
  activityFrag,
  postLinkSource,
  hideArtistOnPill,
}: {
  activityFrag: FragmentType<ProfileCreatedPostFragmentDoc>;
  postLinkSource: PostLinkSource;
  hideArtistOnPill: boolean;
}) => {
  const postCreated = getFragment(ProfileCreatedPostFragmentDoc, activityFrag);
  const { value: playlistPostsEnabled } = useGate(FEATURE_GATES.PLAYLIST_POSTS);
  const content = React.useMemo(() => {
    switch (postCreated.post.__typename) {
      case 'ReleasePost':
        return (
          <ReleaseCreatedPost
            userInfoFrag={postCreated}
            releasePostFrag={postCreated.post}
            postLinkSource={postLinkSource}
            hideArtistOnPill={hideArtistOnPill}
          />
        );
      case 'PlaylistPost':
        if (!playlistPostsEnabled) return null;
        return (
          <PlaylistCreatedPost
            userInfoFrag={postCreated}
            playlistPostFrag={postCreated.post}
            postLinkSource={postLinkSource}
          />
        );

      default:
        return null;
    }
  }, [hideArtistOnPill, postCreated, playlistPostsEnabled, postLinkSource]);

  return content;
};
